import { View } from "@westpierstudio/luma/mvc";
import { getEase, Tween } from "@westpierstudio/luma/createjs";
import { GameServices } from "game/GameServices";
import { ReelSymbol } from "./ReelSymbol";
import { SpinConfig } from "./ReelsModel";
import { ReelSymbols, SymbolID, WildSymbolID } from "servertypes/SharedTypes";
import { mod, randomCoreSymbolID } from "utils/Misc";
import { Signal } from "type-signals";
import { SymbolCache } from "./Reels";
import { getSoundPlayer } from "sound/SoundLocator";
import { cloneDeep, sample } from "lodash";

const SYMBOL_COUNT = 5; //0 is offscreen, 1-4 are in view

const enum State {
    STOP,
    MOVE,
    BOUNCE
}

class ReelSignals {
    scatterWinComplete = new Signal<(reelIndex: number) => void>();
    reelStop = new Signal<(reelIndex: number) => void>();
    reelAnticipateStart = new Signal<(reel: Reel) => void>();
}

export class Reel extends View {
    private readonly defaultY: number;
    private readonly symbols: ReelSymbol[] = [];
    private readonly symbolHeight: number;
    private reelStrip: SymbolID[] = [];
    private reelStripIndex = 0;
    private swappedSymbols: { index: number, symbol: SymbolID }[] = [];
    private spinConfig!: SpinConfig;
    private state = State.STOP;
    private doubleWYFlag = false;
    signals = new ReelSignals();

    constructor(protected override services: GameServices, private readonly id: number, position: { x: number, y: number }, symbolSize: { width: number, height: number },
        symbolCache: SymbolCache) {
        super(services);

        let symbol;

        this.symbolHeight = symbolSize.height;

        for (let i = 0; i < SYMBOL_COUNT; i++) {

            symbol = new ReelSymbol(services, symbolCache);
            symbol.y = i * this.symbolHeight;

            symbol.signals.scatterWinComplete.add(() => this.signals.scatterWinComplete.dispatch(this.id));

            this.addChild(symbol);
            this.symbols.push(symbol);
        }

        this.position = position;
        this.defaultY = this.y;
    }

    get totalSpinTime(): number {

        const { anticipationActive, anticipationTime, baseSpinTime, reelStartDelay, reelStopDelay, reelStopTimeIn, reelStopTimeOut } = this.spinConfig;

        let spinTime = (this.id * reelStartDelay) + baseSpinTime + (this.id * reelStopDelay);
        const stopTime = reelStopTimeIn + reelStopTimeOut;

        if (anticipationActive) {
            spinTime += anticipationTime;
        }

        return spinTime + stopTime;
    }

    setSymbols(symbols: ReelSymbols): void {

        for (const [index, symbolID] of symbols.entries()) {
            this.symbols[index + 1].setSymbol(symbolID);
        }
    }

    private setSymbolsSlam(symbols: ReelSymbols) {
        this.setSymbols(symbols);
        if (this.id !== 0) this.fixRobinhood(false);
    }

    private fixRobinhood(isStartSpin: boolean) {//stops robinhood being cut in half
        if (this.symbols[1].id === WildSymbolID.WY) {

            if (this.symbols[0].id !== WildSymbolID.WX) {
                if (isStartSpin && this.symbols[0].id === WildSymbolID.WY) {
                    //need to check symbol above the WY and replace it with a random core symbol if spin
                    this.doubleWYFlag = true;
                }
                this.symbols[0].setSymbol(WildSymbolID.WX);

            }
        }
        else if (this.symbols[0].id === WildSymbolID.WX) {
            const randSymbol = randomCoreSymbolID();
            this.symbols[0].setSymbol(randSymbol);
        }
    }

    setReelStrip(reelStrip: SymbolID[]): void {

        this.restoreSwappedSymbols(); //Restore any swapped out symbols from the previous spin prior to a strip switch
        this.reelStrip = cloneDeep(reelStrip).reverse();
    }

    setSpinConfig(spinConfig: SpinConfig): void {
        this.spinConfig = spinConfig;
    }

    getRandomReelStripIndex(): number {
        return Math.floor(Math.random() * this.reelStrip.length);
    }

    //finds the stop index of the result
    findReelStopIndex(result: ReelSymbols): number {
        const index = sample(this.findStripStopIndexes(result));
        if (index != undefined) {
            return index;
        }
        else throw `Didnt find stop index`;
    }

    private findStripStopIndexes(result: SymbolID[]): number[] {
        this.restoreSwappedSymbols();

        const reelStrip = this.reelStrip;
        const resultLen = result.length;
        result = cloneDeep(result).reverse();
        const stopIndexMatches: number[] = [];
        let tempMatch = [];
        let i = 0;
        while (i < reelStrip.length + result.length) {
            const index = i < reelStrip.length ? i : i - reelStrip.length;
            const symbol = reelStrip[index];
            if (symbol === result[tempMatch.length]) {
                tempMatch.push(symbol);
                if (tempMatch.length == resultLen) {
                    //match found
                    let matchIndex = i - resultLen + 1;
                    matchIndex = matchIndex % reelStrip.length;
                    stopIndexMatches.push(matchIndex);
                    tempMatch = [];
                    i = i - resultLen + 1;
                }
            }
            else {
                //no match found
                if (tempMatch.length > 0) {
                    i = i - tempMatch.length;
                    tempMatch = [];
                }
            }
            i++;
        }
        // devLog("Matching result "+result +" reels strips in reel " + this.id)
        // devLog(this.reelStrip.length + " elements: "+(this.reelStrip));
        // devLog("Matched:" + stopIndexMatches)
        return stopIndexMatches;
    }

    swapReelStripSymbols(startIndex: number, symbols: SymbolID[]): void {

        //Restore the last swapped symbols prior to the new swap to prevent persistent changes
        this.restoreSwappedSymbols();

        let index = startIndex;

        for (const symbol of symbols) {

            this.swappedSymbols.push({ index, symbol: this.reelStrip[index] });
            this.reelStrip[index] = symbol;

            index++;
            index = index % this.reelStrip.length;
        }
    }

    spin(stopIndex: number): void {

        const { baseSpinTime, reelStartDelay, reelStopDelay, symbolsPerSecond, anticipationActive, anticipationTime, anticipationSymbolsPerSec } = this.spinConfig;

        const totalMoveTimeMS = baseSpinTime + (this.id * reelStopDelay);

        const totalDistance = (symbolsPerSecond * this.symbolHeight) * (totalMoveTimeMS / 1000);
        const symbolMoveCount = Math.floor(totalDistance / this.symbolHeight);
        const oneSymbolMoveTimeMS = (symbolMoveCount > 0) ? totalMoveTimeMS / symbolMoveCount : 0;

        let anticipationSymbolMoveCount = 0, anticipationOneSymbolMoveTimeMS = 0;
        if (anticipationActive) {
            const anticipationTotalDistance = (anticipationSymbolsPerSec * this.symbolHeight) * (anticipationTime / 1000);
            anticipationSymbolMoveCount = Math.floor(anticipationTotalDistance / this.symbolHeight);
            anticipationOneSymbolMoveTimeMS = (anticipationSymbolMoveCount > 0) ? anticipationTime / anticipationSymbolMoveCount : 0;
        }

        //We want the stopindex to correspond with the bottom symbol so inlcude a shift down of 3
        this.reelStripIndex = mod(stopIndex - symbolMoveCount - anticipationSymbolMoveCount + 4, this.reelStrip.length);

        this.setNextOffScreenSymbol();
        this.fixRobinhood(true);
        this.startMoveTween((this.id * reelStartDelay), oneSymbolMoveTimeMS, symbolMoveCount, anticipationActive, anticipationOneSymbolMoveTimeMS, anticipationSymbolMoveCount);
    }

    slamStop(symbols: ReelSymbols): void {

        if (this.state === State.MOVE) {

            Tween.removeTweens(this);
            this.y = this.defaultY;

            this.setSymbolsSlam(symbols);
            this.stopWithBounce(false);
            getSoundPlayer().skipAnticipate();
        }
    }

    startWinAnimations(animateState: boolean[], loop: boolean): void {

        for (const [i, animate] of animateState.entries()) {

            if (animate) {
                this.symbols[i + 1].startWinAnim(loop);
            }
        }
    }

    startBonusLandAnimation() {
        for (const symbol of this.symbols) {
            symbol.playBonusLandAnim();
        }
    }

    startScatterWinAnimationSequence(row: number) {
        this.symbols[row + 1].scatterWinSequence();
    }

    startScatterWinAnimationLoop(row: number) {
        this.symbols[row + 1].scatterWinLoop();
    }

    stopWinAnimations(): void {

        for (const [i, symbol] of this.symbols.entries()) {

            if (i > 0) {
                symbol.stopWinAnimLoop();
            }
        }
    }

    bonusCompleteAnimation(row: number) {
        this.symbols[row + 1].bonusCompleteAnimation();
    }

    bonusExtraFreeSpinsAnimation(row: number) {
        this.symbols[row + 1].bonusExtraFreeSpinsAnimation();
    }

    bonusInitiatedAnimation(row: number) {
        this.symbols[row + 1].bonusInitiatedAnimation();
    }


    override reset(): void {

        this.reelStrip = [];
        this.reelStripIndex = 0;
        this.swappedSymbols = [];

        Tween.removeTweens(this);
        this.y = this.defaultY;
        this.state = State.STOP;

        for (const symbol of this.symbols) {
            symbol.reset();
        }
    }

    private startMoveTween(startDelay: number, oneSymbolMoveTimeMS: number, symbolMoveCount: number, anticipationActive: boolean, anticipationOneSymbolMoveTimeMS: number, anticipationSymbolMoveCount: number): void {

        this.state = State.MOVE;
        const targetY = this.y + this.symbolHeight;
        const moveTween = Tween.get(this);
        moveTween.wait(startDelay);

        this.moveTween(moveTween, symbolMoveCount, targetY, oneSymbolMoveTimeMS);
        if (anticipationActive) {
            moveTween.call(() => this.signals.reelAnticipateStart.dispatch(this))
                .call(() => getSoundPlayer().anticipate());
            this.moveTween(moveTween, anticipationSymbolMoveCount, targetY, anticipationOneSymbolMoveTimeMS);
        }

        moveTween.call(() => {
            this.playReelStopSound();
            this.stopWithBounce(anticipationActive);
        });
    }

    private playReelStopSound() {
        getSoundPlayer().reelStop();
        if (this.spinConfig.bonusHit > 0) {
            getSoundPlayer().bonusHit(this.spinConfig.bonusHit);
        }
    }

    private moveTween(inputMoveTween: createjs.Tween, symbolMoveCount: number, targetY: number, oneSymbolMoveTimeMS: number) {
        while (symbolMoveCount > 0) {

            inputMoveTween.to({ y: targetY }, oneSymbolMoveTimeMS);
            inputMoveTween.call(() => {
                this.setNextReelStripIndex();
                this.updateSymbolsInView();
                this.setNextOffScreenSymbol();
            });
            inputMoveTween.to({ y: this.defaultY }, 0);

            symbolMoveCount--;
        }
        return symbolMoveCount;
    }

    private restoreSwappedSymbols(): void {

        if (this.swappedSymbols.length > 0) {

            for (const item of this.swappedSymbols) {
                this.reelStrip[item.index] = item.symbol;
            }

            this.swappedSymbols = [];
        }
    }

    private setNextReelStripIndex(): void {

        this.reelStripIndex++;
        this.reelStripIndex = this.reelStripIndex % this.reelStrip.length;
    }

    private setNextOffScreenSymbol(): void {
        if (this.doubleWYFlag) {
            this.doubleWYFlag = false;
            const randSymbol = randomCoreSymbolID();
            this.symbols[0].setSymbol(randSymbol);
        }
        else {
            this.symbols[0].setSymbol(this.reelStrip[this.reelStripIndex]);
        }
    }

    //Set each symbol id to the one preceding it
    private updateSymbolsInView(): void {

        let symbolID;

        for (let i = SYMBOL_COUNT - 1; i > 0; i--) {

            symbolID = this.symbols[i - 1].id;
            this.symbols[i].setSymbol(symbolID);
        }
    }

    private stopWithBounce(anticipation: boolean): void {
        this.state = State.BOUNCE;

        const { reelStopDistance, reelStopEaseIn, reelStopEaseOut, reelStopTimeIn, reelStopTimeOut, anticipationStopTimeIn, anticipationStopTimeOut, bonusHit } = this.spinConfig;
        const timeIn = anticipation ? anticipationStopTimeIn : reelStopTimeIn;
        const timeOut = anticipation ? anticipationStopTimeOut : reelStopTimeOut;
        if (bonusHit > 0) {
            this.startBonusLandAnimation();
        }
        Tween.get(this)
            .to({ y: this.y + reelStopDistance }, timeIn, getEase(reelStopEaseIn))
            .to({ y: this.y }, timeOut, getEase(reelStopEaseOut))
            .call(() => {
                this.state = State.STOP;
                this.signals.reelStop.dispatch(this.id);
            });
    }
}